import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { useTheme, useThemeStyles } from '@/context/theme-context'
import UniversalHeader from '@/components/shared/UniversalHeader'
import UniversalFooter from '@/components/shared/UniversalFooter'
import { Shield, Brain, Palette } from 'lucide-react'

export function ThemeDemo() {
  const { theme, actualTheme, resetTheme } = useTheme()
  const { colors, isDark } = useThemeStyles()

  const demoColors = [
    { name: 'Primary', value: colors.brand.primary, class: 'bg-brand-primary' },
    { name: 'Secondary', value: colors.brand.secondary, class: 'bg-brand-secondary' },
    { name: 'Tertiary', value: colors.brand.tertiary, class: 'bg-brand-tertiary' },
    { name: 'Success', value: colors.status.success, class: 'bg-status-success' },
  ]

  return (
    <div className="min-h-screen flex flex-col bg-bg-primary">
      <UniversalHeader />
      <div className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              CareVault Theme System
            </h1>
            <p className="text-text-secondary">
              Current: {theme} | Active: {actualTheme} | Dark: {isDark ? 'Yes' : 'No'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            <Button onClick={resetTheme} variant="outline" size="sm">
              Reset
            </Button>
          </div>
        </div>

        {/* Color Palette */}
        <Card className="bg-card-bg border-card-border">
          <CardHeader>
            <CardTitle className="text-text-primary flex items-center space-x-2">
              <Palette className="h-5 w-5" />
              <span>Color Palette</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {demoColors.map((color) => (
                <div key={color.name} className="text-center space-y-2">
                  <div className={`w-full h-16 rounded-lg ${color.class} shadow-lg`} />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{color.name}</p>
                    <p className="text-xs text-text-secondary font-mono">hsl({color.value})</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Component Examples */}
        <Card className="bg-card-bg border-card-border">
          <CardHeader>
            <CardTitle className="text-text-primary">Component Examples</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button className="bg-btn-primary-bg text-btn-primary-fg">Primary</Button>
              <Button variant="outline">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-stats-bg border-stats-border">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-brand-primary/10 rounded-lg">
                      <Shield className="h-5 w-5 text-brand-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">Security</p>
                      <p className="text-sm text-text-secondary">Encrypted</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-stats-bg border-stats-border">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-brand-secondary/10 rounded-lg">
                      <Brain className="h-5 w-5 text-brand-secondary" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">AI Powered</p>
                      <p className="text-sm text-text-secondary">Smart insights</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="bg-[#0F172A]">
        <UniversalFooter />
      </div>
    </div>
  )
}